import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { CopyButton } from "../components/CopyButton";

// ---------------------------------------------------------------- content

const CORE_CARDS = [
  {
    n: "01",
    title: "Challenge objective",
    body: "What is the programme trying to improve, transform or de-risk?",
  },
  {
    n: "02",
    title: "Evidence and data custody",
    body: "Which organisations hold the evidence, data, permissions, approvals or constraints?",
  },
  {
    n: "03",
    title: "Authority and role map",
    body: "Who may request, approve, validate, use or rely on evidence?",
  },
  {
    n: "04",
    title: "AI / tool boundary",
    body: "Where can AI assist, where must roles decide, and where should small or specialist models be used instead of unnecessary LLMs?",
  },
  {
    n: "05",
    title: "Change and dependency control",
    body: "How are requirements, dependencies, prompts, code, models, workflows and assumptions controlled over the life of the project?",
  },
  {
    n: "06",
    title: "Receipts and measurable impact",
    body: "What predictive and realised impacts must be recorded, reported and proven?",
  },
];

const BRANCHES = [
  {
    id: "horizon",
    href: "/fit/horizon",
    label: "Horizon Europe",
    title: "Horizon Europe fit: governed coordination for multi-party innovation",
    body: "KATLAS can lead a work package on operational governance, trusted evidence coordination, bounded AI/tool use and lifecycle reporting for Cluster 4 and Cluster 6 consortia. Data can stay local; roles remain accountable; dependencies and outcomes are tracked; receipts prove what happened.",
  },
  {
    id: "esrc",
    href: "/fit/esrc-future-data-services",
    label: "ESRC Future Data Services",
    title: "ESRC Future Data Services: receipt-backed federated access",
    body: "A governed access model for improving discovery, permissioned use and evidence-led coordination across existing ESRC social science data service infrastructures. The aim is not to create another data service, but to enhance existing services with role-governed access, bounded AI assistance, local data custody, staff upskilling and receipt-backed reporting.",
  },
];

const SUMMARY_TEXT = [
  "KATLAS — Calls / Challenges Fit",
  "",
  "KATLAS is best suited to challenges where several organisations, roles, systems or agents must coordinate safely around shared objectives without forcing all data into one central platform.",
  "",
  "The fit is strongest where success depends on clear custody of evidence, defined authority to act, bounded use of AI or digital tools, and receipts that prove what was requested, approved, shared, withheld, completed or escalated.",
  "",
  "This makes KATLAS relevant to challenge calls involving regulated collaboration, operational resilience, trusted data sharing, AI governance, infrastructure coordination, public-sector redesign, supply-chain assurance, health and care pathways, and multi-party reporting.",
  "",
  ...CORE_CARDS.map((c) => `${c.n} ${c.title}\n${c.body}`),
  "",
  "AI assists. Roles decide. Receipts prove.",
  "",
  "Branches",
  ...BRANCHES.map((b) => `- ${b.label}: https://platform.katlastechnology.com${b.href}`),
  "",
  "https://platform.katlastechnology.com/fit",
].join("\n");

// ---------------------------------------------------------------- card

const Card = ({ n, title, body, accent = "blue", testId }) => (
  <article
    data-testid={testId || `fit-card-${n}`}
    className="bg-[#020B14] border border-white/[0.08] p-5 sm:p-6"
  >
    <div className="flex items-baseline gap-3 mb-2">
      <span
        className={`mono text-[10px] uppercase tracking-[0.22em] ${
          accent === "amber" ? "text-[#EAB308]" : "text-[#0066FF]"
        }`}
      >
        {n}
      </span>
      <span className="h-px flex-1 bg-white/10" />
    </div>
    <h3 className="font-display text-[17px] sm:text-lg text-white leading-snug">
      {title}
    </h3>
    <p className="mt-2.5 text-sm text-slate-300 leading-relaxed">{body}</p>
  </article>
);

// ---------------------------------------------------------------- page

export default function FitIndex() {
  return (
    <div className="min-h-screen bg-[#020B14] text-white" id="top">
      <Nav />
      <main className="pt-24 pb-24">
        <div className="max-w-[760px] mx-auto px-5 sm:px-8">
          {/* Breadcrumb + meta */}
          <div className="flex items-center gap-3 mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-8 flex-wrap">
            <Link to="/" data-testid="crumb-home" className="hover:text-white">
              Platform
            </Link>
            <span>/</span>
            <span className="text-slate-200">Calls / Challenges Fit</span>
            <span className="ml-auto text-slate-500">≈ 2 min read</span>
          </div>

          {/* Hero */}
          <header className="mb-10">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#0066FF]">
              Where KATLAS fits
            </div>
            <h1
              data-testid="fit-headline"
              className="font-display text-[28px] sm:text-4xl mt-4 leading-[1.15]"
            >
              Calls / Challenges Fit
            </h1>
            <p
              data-testid="fit-subhead"
              className="mt-5 text-[15px] sm:text-base text-slate-200 leading-relaxed"
            >
              KATLAS is best suited to challenges where several
              organisations, roles, systems or agents must coordinate safely
              around shared objectives without forcing all data into one
              central platform.
            </p>
            <p
              data-testid="fit-fit-strongest"
              className="mt-4 text-sm sm:text-[15px] text-slate-300 leading-relaxed"
            >
              The fit is strongest where success depends on clear custody of
              evidence, defined authority to act, bounded use of AI or
              digital tools, and{" "}
              <span className="text-[#EAB308]">
                receipts that prove what was requested, approved, shared,
                withheld, completed or escalated.
              </span>
            </p>
            <p
              data-testid="fit-relevance"
              className="mt-4 text-sm sm:text-[15px] text-slate-300 leading-relaxed"
            >
              This makes KATLAS relevant to challenge calls involving
              regulated collaboration, operational resilience, trusted data
              sharing, AI governance, infrastructure coordination,
              public-sector redesign, supply-chain assurance, health and
              care pathways, and multi-party reporting.
            </p>

            <div
              data-testid="fit-strap"
              className="mt-6 inline-flex flex-wrap items-center gap-x-2.5 gap-y-1.5 border border-white/[0.08] bg-white/[0.02] px-3 py-2"
            >
              <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                Multi-party coordination
              </span>
              <span className="text-slate-700" aria-hidden>·</span>
              <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                Evidence at source
              </span>
              <span className="text-slate-700" aria-hidden>·</span>
              <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                Bounded AI
              </span>
              <span className="text-slate-700" aria-hidden>·</span>
              <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                Receipts
              </span>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-2.5">
              <CopyButton text={SUMMARY_TEXT} label="Copy summary" testId="copy-summary-btn" />
              <CopyButton
                text="https://platform.katlastechnology.com/fit"
                label="Copy share link"
                testId="copy-share-btn"
              />
            </div>
          </header>

          {/* Core qualification cards */}
          <section className="grid grid-cols-1 gap-3.5" data-testid="core-cards">
            {CORE_CARDS.map((c) => (
              <Card key={c.n} {...c} testId={`core-card-${c.n}`} />
            ))}
          </section>

          {/* Footer line */}
          <section className="mt-10 border border-[#0066FF]/30 bg-[#0066FF]/[0.06] p-5 sm:p-6">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#0066FF]">
              Operating line
            </div>
            <p
              data-testid="fit-operating-line"
              className="mt-3 font-display text-lg sm:text-xl text-white leading-snug"
            >
              AI assists.
              <br className="sm:hidden" /> Roles decide.
              <br className="sm:hidden" />{" "}
              <span className="text-[#EAB308]">Receipts prove.</span>
            </p>
          </section>

          {/* Branches */}
          <section className="mt-12" data-testid="branches">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#0066FF]">
              Branches
            </div>
            <h2 className="font-display text-2xl sm:text-3xl mt-3 leading-tight">
              Choose a call or challenge.
            </h2>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
              The same qualification method runs across all calls. Each
              branch frames the specific programme intent, the work package
              KATLAS can lead and the partner-fit expectations.
            </p>

            <div className="mt-6 grid grid-cols-1 gap-4">
              {BRANCHES.map((b) => (
                <Link
                  key={b.id}
                  to={b.href}
                  data-testid={`branch-${b.id}`}
                  className="block border border-white/[0.1] hover:border-[#0066FF]/60 bg-[#071627] hover:bg-[#0D2035] p-5 sm:p-6 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="mono text-[10px] uppercase tracking-[0.22em] text-[#0066FF]">
                      {b.label}
                    </span>
                    <svg
                      width="14" height="14" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2"
                      className="text-slate-300"
                    >
                      <path d="M5 12h14M13 5l7 7-7 7" />
                    </svg>
                  </div>
                  <h3 className="font-display text-lg sm:text-xl text-white mt-2 leading-snug">
                    {b.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                    {b.body}
                  </p>
                </Link>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="mt-12 border-t border-white/[0.08] pt-8">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#0066FF]">
              Explore fit with KATLAS
            </div>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">
              Use this page to test whether KATLAS should lead a work
              package on operational governance, evidence coordination,
              bounded AI/tool use and lifecycle reporting for your call or
              challenge.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
              <a
                href="mailto:edward.cole@katlastechnology.io?subject=Calls%2FChallenges%20Fit%20walkthrough"
                data-testid="fit-walkthrough-cta"
                className="cta-primary inline-flex items-center justify-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] text-white px-5 py-3 text-sm font-medium rounded-sm"
              >
                Request a walkthrough
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
              </a>
            </div>
          </section>

          {/* Bottom navigation */}
          <nav className="mt-12 pt-6 border-t border-white/[0.08] flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <Link
              to="/"
              data-testid="back-platform-link"
              className="mono text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-white inline-flex items-center gap-2"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back to Platform
            </Link>
            <a
              href="https://clubcierge.katlastechnology.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="fit-live-link"
              className="mono text-[11px] uppercase tracking-[0.2em] text-[#0066FF] hover:text-white inline-flex items-center gap-2"
            >
              <span className="w-1.5 h-1.5 bg-[#0066FF] pulse-dot rounded-full" />
              See live ClubCierge proof
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 17L17 7M9 7h8v8"/></svg>
            </a>
          </nav>
        </div>
      </main>
      <Footer />
    </div>
  );
}
